import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";

const ExternalTicketTransferModal = ({ open, onClose, ticketId }) => {
  const [platforms, setPlatforms] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPlatformId, setSelectedPlatformId] = useState("");
  const [selectedQueueId, setSelectedQueueId] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTargets, setLoadingTargets] = useState(false);

  useEffect(() => {
    const loadPlatforms = async () => {
      if (!open) return;

      try {
        const { data } = await api.get("/external-platforms");
        setPlatforms((data || []).filter(platform => platform.active !== false));
      } catch (err) {
        toastError(err);
      }
    };

    loadPlatforms();
  }, [open]);

  useEffect(() => {
    const loadTargets = async () => {
      setQueues([]);
      setUsers([]);
      setSelectedQueueId("");
      setSelectedUserId("");

      if (!open || !selectedPlatformId) return;

      setLoadingTargets(true);
      try {
        const [queuesResponse, usersResponse] = await Promise.all([
          api.get(`/external-platforms/${selectedPlatformId}/queues`),
          api.get(`/external-platforms/${selectedPlatformId}/users`)
        ]);

        setQueues(queuesResponse.data || []);
        setUsers(usersResponse.data || []);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingTargets(false);
      }
    };

    loadTargets();
  }, [open, selectedPlatformId]);

  const handleClose = () => {
    setSelectedPlatformId("");
    setSelectedQueueId("");
    setSelectedUserId("");
    setQueues([]);
    setUsers([]);
    onClose();
  };

  const handlePlatformChange = e => {
    setSelectedPlatformId(e.target.value);
  };

  const handleQueueChange = e => {
    setSelectedQueueId(e.target.value);
    setSelectedUserId("");
  };

  const filteredUsers = selectedQueueId
    ? users.filter(user =>
        (user.queues || []).some(queue => String(queue.id) === String(selectedQueueId))
      )
    : users;

  const handleTransfer = async () => {
    if (!ticketId || !selectedPlatformId) return;

    setLoading(true);
    try {
      await api.post(`/tickets/${ticketId}/transfer-external`, {
        externalPlatformId: selectedPlatformId,
        targetQueueId: selectedQueueId || null,
        targetUserId: selectedUserId || null
      });
      toast.success("Conversa transferida para a plataforma externa.");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transferir para plataforma externa</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" style={{ marginBottom: 16 }}>
          Selecione a plataforma que recebera uma copia do contato, atendimento e
          mensagens desta conversa.
        </Typography>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Plataforma</InputLabel>
          <Select
            value={selectedPlatformId}
            onChange={handlePlatformChange}
            label="Plataforma"
          >
            <MenuItem value="">
              <em>Selecione</em>
            </MenuItem>
            {platforms.map(platform => (
              <MenuItem key={platform.id} value={platform.id}>
                {platform.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {loadingTargets && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Carregando destino...</Typography>
          </div>
        )}

        <FormControl
          variant="outlined"
          fullWidth
          style={{ marginTop: 16 }}
          disabled={!selectedPlatformId || loadingTargets}
        >
          <InputLabel>Departamento</InputLabel>
          <Select
            value={selectedQueueId}
            onChange={handleQueueChange}
            label="Departamento"
          >
            <MenuItem value="">
              <em>Sem departamento</em>
            </MenuItem>
            {queues.map(queue => (
              <MenuItem key={queue.id} value={queue.id}>
                {queue.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          fullWidth
          style={{ marginTop: 16 }}
          disabled={!selectedPlatformId || loadingTargets}
        >
          <InputLabel>Usuario</InputLabel>
          <Select
            value={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value)}
            label="Usuario"
          >
            <MenuItem value="">
              <em>Sem usuario</em>
            </MenuItem>
            {filteredUsers.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="secondary"
          variant="outlined"
          disabled={loading}
        >
          Cancelar
        </Button>
        <ButtonWithSpinner
          color="primary"
          variant="contained"
          loading={loading}
          disabled={loading || loadingTargets || !selectedPlatformId}
          onClick={handleTransfer}
        >
          Transferir
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default ExternalTicketTransferModal;
